import { LitElement, html, svg, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { keyed } from "lit/directives/keyed.js";
import { i18n } from "../i18n";

type Wallpaper = "desktop" | "stars" | "pipes" | "maze";
const STORAGE_KEY = "cpl.login.wallpaper";

@customElement("login-wallpaper")
export class LoginWallpaper extends LitElement {
  @property({ type: Boolean }) active = false;
  @state() private desktopWidth = Math.max(
    540,
    Math.round((window.innerWidth / window.innerHeight) * 900),
  );
  @state() private mode: Wallpaper = "desktop";
  @state() private paused = false;

  @state() private pageHidden = document.hidden;
  @state() private rendererUnavailable = false;
  private renderer?: { advance(seconds: number): void; dispose(): void };
  private animation = 0;
  private lastFrame = 0;
  private generation = 0;

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.paused = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (
        saved === "desktop" ||
        saved === "stars" ||
        saved === "pipes" ||
        saved === "maze"
      ) {
        this.mode = saved;
      }
      const paused = localStorage.getItem(`${STORAGE_KEY}.paused`);
      if (paused !== null) this.paused = paused === "true";
    } catch {
      // The background also works when browser storage is disabled.
    }
    window.addEventListener("languagechange", this.changed);
    window.addEventListener("resize", this.resizeDesktop);
    document.addEventListener("visibilitychange", this.visibility);
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.changed);
    window.removeEventListener("resize", this.resizeDesktop);
    document.removeEventListener("visibilitychange", this.visibility);
    this.stopRenderer();
    super.disconnectedCallback();
  }

  private resizeDesktop = () => {
    this.desktopWidth = Math.max(
      540,
      Math.round((window.innerWidth / window.innerHeight) * 900),
    );
  };

  private visibility = () => {
    this.pageHidden = document.hidden;
  };

  protected updated(changed: PropertyValues) {
    if (changed.has("mode")) {
      this.stopRenderer();
      this.rendererUnavailable = false;
      if (this.mode !== "desktop") void this.startRenderer();
    }
    this.schedule();
  }

  private async startRenderer() {
    const generation = this.generation;
    try {
      const Renderer =
        this.mode === "stars"
          ? (await import("./login-stars")).LoginStars
          : this.mode === "maze"
            ? (await import("./login-maze")).LoginMaze
            : (await import("./login-pipes")).LoginPipes;
      if (generation !== this.generation || !this.isConnected) return;
      const canvas = this.querySelector("canvas");
      if (!canvas) return;
      this.renderer = new Renderer(canvas);
      this.schedule();
    } catch {
      if (generation === this.generation && this.isConnected)
        this.rendererUnavailable = true;
    }
  }

  private stopRenderer() {
    this.generation++;
    cancelAnimationFrame(this.animation);
    this.animation = 0;
    this.renderer?.dispose();
    this.renderer = undefined;
  }

  private schedule() {
    cancelAnimationFrame(this.animation);
    this.animation = 0;
    this.lastFrame = 0;
    if (this.renderer && !this.paused && !this.pageHidden) {
      this.animation = requestAnimationFrame(this.frame);
    }
  }

  private frame = (now: number) => {
    if (!this.renderer || this.paused || this.pageHidden || !this.isConnected)
      return;
    if (!this.lastFrame) this.lastFrame = now;
    if (now - this.lastFrame >= 1000 / 30) {
      this.renderer.advance(Math.min((now - this.lastFrame) / 1000, 0.1));
      this.lastFrame = now;
    }
    this.animation = requestAnimationFrame(this.frame);
  };

  private activateSwitcher = () =>
    this.dispatchEvent(
      new CustomEvent("wallpaper-focus", {
        bubbles: true,
        composed: true,
      }),
    );

  private changed = () => this.requestUpdate();

  private select(mode: Wallpaper) {
    this.mode = mode;
    this.save();
  }

  private save() {
    try {
      localStorage.setItem(STORAGE_KEY, this.mode);
      localStorage.setItem(`${STORAGE_KEY}.paused`, String(this.paused));
    } catch {
      // Selection still applies for the current page.
    }
  }

  private desktop() {
    const zh = i18n.t("wallpaperDesktop") === "桌面";
    return svg`
      <defs>
        <symbol id="cpl-folder" viewBox="0 0 48 40">
          <path d="M3 8V3h18l5 5h19v28H3z" fill="#c4a540" stroke="#403518" stroke-width="2" />
          <path d="M2 14h44l-5 23H6z" fill="#f5df8a" stroke="#403518" stroke-width="2" />
          <path d="M5 16h37" stroke="#fff4c1" stroke-width="2" />
        </symbol>
        <symbol id="cpl-computer" viewBox="0 0 48 48">
          <path d="M6 1h36v29H6zM20 30h8v6h-8zM2 37h44v9H2z" fill="#c0c0c0" stroke="#111" stroke-width="2" />
          <path d="M10 5h28v20H10z" fill="#000080" stroke="#808080" stroke-width="2" />
          <path d="M12 22V8h23" fill="none" stroke="#55d7e1" stroke-width="2" />
          <path d="M7 39h24m7 3h4" stroke="#fff" stroke-width="2" />
        </symbol>
        <symbol id="cpl-bin" viewBox="0 0 48 48">
          <path d="M9 11h30l-4 33H13z" fill="#c0c0c0" stroke="#111" stroke-width="2" />
          <path d="M7 8h34v5H7zM18 3h12v5H18z" fill="#dfdfdf" stroke="#111" stroke-width="2" />
          <path d="M17 17v20m7-20v20m7-20v20" stroke="#808080" stroke-width="2" />
          <path d="m18 24 6-5 6 5-6 7z" fill="#008000" />
        </symbol>
        <symbol id="cpl-paper" viewBox="0 0 26 32">
          <path d="M1 1h17l7 7v23H1z" fill="#fff" stroke="#000" stroke-width="2" />
          <path d="M18 1v8h7M5 14h15M5 19h15M5 24h10" fill="none" stroke="#808080" stroke-width="2" />
        </symbol>
        <clipPath id="cpl-monitor-clip"><rect x="18" y="43" width="280" height="107" /></clipPath>
        <clipPath id="cpl-copy-clip"><rect x="20" y="135" width="282" height="19" /></clipPath>
      </defs>
      <g font-family="Tahoma, sans-serif" font-size="14" fill="#fff" text-anchor="middle">
        <use href="#cpl-computer" x="52" y="42" width="48" height="48" />
        <text x="76" y="111">${zh ? "我的电脑" : "My Computer"}</text>
        <use href="#cpl-folder" x="52" y="155" width="48" height="40" />
        <text x="76" y="221">${zh ? "我的文档" : "My Documents"}</text>
        <use href="#cpl-bin" x="52" y="268" width="48" height="48" />
        <text x="76" y="337">${zh ? "回收站" : "Recycle Bin"}</text>
      </g>
      <g transform="translate(48 615)" font-family="Tahoma, sans-serif" font-size="13">
        <path d="M0 0h326v198H0z" fill="#c0c0c0" stroke="#000" stroke-width="2" />
        <path d="M1 196V1h323" fill="none" stroke="#fff" stroke-width="2" />
        <path d="M4 4h318v23H4z" fill="#808080" />
        <text x="12" y="20" fill="#fff" font-weight="bold">${zh ? "正在复制文件…" : "Copying files…"}</text>
        <path d="M300 8h17v15h-17z" fill="#c0c0c0" stroke="#fff" />
        <path d="m305 12 7 7m0-7-7 7" stroke="#000" stroke-width="2" />
        <use href="#cpl-folder" x="24" y="66" width="52" height="44" />
        <use href="#cpl-folder" x="250" y="66" width="52" height="44" />
        ${[0, -1.2, -2.4].map(
          (
            delay,
          ) => svg`<g class="wallpaper-copy-file" style=${`animation-delay:${delay}s`}>
          <use href="#cpl-paper" x="52" y="44" width="22" height="28" />
        </g>`,
        )}
        <text x="20" y="127" fill="#000">${zh ? "从“我的文档”到“备份”" : "From “Documents” to “Backup”"}</text>
        <path d="M18 133h286v23H18z" fill="#fff" stroke="#808080" stroke-width="2" />
        <g clip-path="url(#cpl-copy-clip)">
          <g class="wallpaper-copy-progress">
            ${Array.from({ length: 30 }, (_, i) => svg`<rect x=${i * 12 - 60} y="135" width="9" height="19" fill="#000080" />`)}
          </g>
        </g>
        <text x="20" y="181" fill="#404040">${zh ? "正在整理桌面文件" : "Organizing desktop files"}</text>
      </g>
      <g class="wallpaper-desktop-monitor" transform=${`translate(${this.desktopWidth - 370} 94)`} font-family="Tahoma, sans-serif" font-size="13">
        <path d="M0 0h316v196H0z" fill="#c0c0c0" stroke="#000" stroke-width="2" />
        <path d="M1 194V1h312" fill="none" stroke="#fff" stroke-width="2" />
        <path d="M4 4h308v23H4z" fill="#808080" />
        <text x="12" y="20" fill="#fff" font-weight="bold">${zh ? "系统监视器" : "System Monitor"}</text>
        <path d="M17 42h282v109H17z" fill="#001800" stroke="#808080" stroke-width="2" />
        <g clip-path="url(#cpl-monitor-clip)">
          <path d="M18 65h280M18 86h280M18 107h280M18 128h280M58 43v107M98 43v107M138 43v107M178 43v107M218 43v107M258 43v107" stroke="#134013" />
          <g class="wallpaper-monitor-trace" fill="none" stroke="#39ff39" stroke-width="2">
            ${[0, 280].map((x) => svg`<path transform=${`translate(${x} 0)`} d="M18 128h20l8-15 10 9 12-49 8 34 13-9 8 32h20l9-17 11 8 10-60 10 35 8-4 8 38h25l10-20 9 11 13-32 9 21 8-5 12 27h31" />`)}
          </g>
        </g>
        <rect x="18" y="168" width="9" height="9" fill="#008000" />
        <text x="34" y="178" fill="#000">${zh ? "处理器使用率" : "Processor usage"}</text>
      </g>`;
  }

  private stars() {
    // Static fallback for browsers without a usable drawing context.
    return svg`${Array.from(
      { length: 80 },
      (_, index) => svg`
      <circle cx=${(index * 233 + 47) % 1440} cy=${(index * 157 + 73) % 900}
        r=${index % 5 === 0 ? 2 : 1} fill="#d4e7ec" />
    `,
    )}`;
  }

  render() {
    const labels = {
      desktop: "wallpaperDesktop",
      stars: "wallpaperStars",
      pipes: "wallpaperPipes",
      maze: "wallpaperMaze",
    } as const;
    return html`
      <div
        class="login-wallpaper-art ${this.mode} ${this.paused || this.pageHidden ? "paused" : ""}"
        aria-hidden="true"
      >
        ${
          this.mode !== "desktop" && !this.rendererUnavailable
            ? keyed(this.mode, html`<canvas aria-hidden="true"></canvas>`)
            : html`<svg
                viewBox=${this.mode === "desktop" ? `0 0 ${this.desktopWidth} 900` : "0 0 1440 900"}
                preserveAspectRatio="xMidYMid slice"
                focusable="false"
              >
                ${this.mode === "desktop" ? this.desktop() : this.stars()}
              </svg>`
        }
      </div>
      <section
        class="wallpaper-switcher ${this.active ? "active" : ""}"
        @pointerdown=${this.activateSwitcher}
        @focusin=${this.activateSwitcher}
        aria-label=${i18n.t("wallpaperTitle")}
      >
        <div class="wallpaper-switcher-title">
          <span aria-hidden="true">▧</span> ${i18n.t("wallpaperTitle")}
        </div>
        <div class="wallpaper-switcher-controls">
          ${(["desktop", "stars", "pipes", "maze"] as const).map(
            (mode) =>
              html` <button
                type="button"
                aria-pressed=${this.mode === mode}
                @click=${() => this.select(mode)}
              >
                ${i18n.t(labels[mode])}
              </button>`,
          )}
          <button
            type="button"
            aria-pressed=${this.paused}
            aria-label=${i18n.t(this.paused ? "wallpaperPlay" : "wallpaperPause")}
            title=${i18n.t(this.paused ? "wallpaperPlay" : "wallpaperPause")}
            @click=${() => {
              this.paused = !this.paused;
              this.save();
            }}
          >
            <span aria-hidden="true">${this.paused ? "▶" : "Ⅱ"}</span>
          </button>
        </div>
      </section>
    `;
  }
}
